import { cloneDeep, isEmpty, isNull } from 'lodash';
import { DEFAULT_COUNTS } from './constants';

/**
 *
 * @name createReportList
 * @param reports {array} - список от counts без сервисов
 * @param categories {array} - список сервисов с категориями
 * @returns {*|*[]}
 */
export const createReportList = (reports, categories) => {
  const reportsObj = {};
  reports.forEach((report) => {
    reportsObj[report.service_id] = report;
  })

  const servicesReports = categories.reduce((acc, category) => {
    if (category.services && category.services.length) {
      const categoryServices = category.services.map((service) => {
        const isServiceReport = !!reportsObj[service.id];
        const serviceReport = reportsObj[service.id] || DEFAULT_COUNTS;

        if (isServiceReport) {
          delete reportsObj[service.id];
        }

        return {
          ...service,
          category_title: category.title,
          category_id: category.id,
          status: category.visibility && service.status === 1 ? 1 : 0,
          is_deleted: false,
          ...serviceReport,
          count_orders: parseFloat(serviceReport.count_orders),
          charge: parseFloat(serviceReport.charge),
          cost: parseFloat(serviceReport.cost),
          profit: parseFloat(serviceReport.profit),
          refunded: parseFloat(serviceReport.refunded),
        };
      })
      return [...acc, ...categoryServices];
    }
    return acc;
  }, [])

  if (isEmpty(reportsObj)) {
    return servicesReports;
  }

  const deletedReports = Object.values(reportsObj).map((report) => ({
    ...report,
    id: report.service_id,
    category_title: 'DELETED',
    provider_id: null,
    is_deleted: true,
    name: '',
    category_id: 0,
    status: 0,
    count_orders: parseFloat(report.count_orders),
    charge: parseFloat(report.charge),
    cost: parseFloat(report.cost),
    profit: parseFloat(report.profit),
    refunded: parseFloat(report.refunded),
  }))
  return [...servicesReports, ...deletedReports]
}

/**
 *
 * @name filterCategories
 * @param service {object} - сервис после объединения с reports и листингом
 * @param categories {array} - массив из ID категорий
 * @returns {*|boolean}
 */
const filterCategories = (service, categories) => {
  if (categories?.length) {
    const categoryId = Number(service.category_id);
    return categories.includes(categoryId);
  }
  return true;
}

/**
 *
 * @name filterProvider
 * @param service {object} - сервис после объединения с reports и листингом
 * @param providers {array} - массив из ID провайдеров
 * @returns {*|boolean}
 */
const filterProvider = (service, providers) => {
  if (providers?.length) {
    const providerId = Number(service.provider_id);
    return providers.includes(providerId);
  }
  return true;
}

/**
 *
 * @name filterStatus
 * @param service {object} - сервис после объединения с reports и листингом
 * @param status {number} - ID статуса
 * @returns {*|boolean}
 */
const filterStatus = (service, status) => {
  if (!isNull(status)) {
    return service.status === status;
  }
  return true;
}

/**
 *
 * @name filterQuery
 * @param service {object} - сервис после объединения с reports и листингом
 * @param query {string} - Строка поиска
 * @param query_type {object} - Тип поиска (Имя сервис, ID)
 * @returns {*|boolean}
 */
const filterQuery = (service, query, query_type) => {
  if (query) {
    if (service.name) {
      switch (Number(query_type)) {
      case 1: {
        const serviceNameLowerCase = service.name.toLowerCase();
        const queryLowerCase = query.toLowerCase();
        return serviceNameLowerCase.indexOf(queryLowerCase) !== -1;
      }
      case 2: {
        const queryNumber = Number(query);
        // eslint-disable-next-line no-restricted-globals
        if (isNaN(queryNumber)) {
          return false;
        }
        return Number(service.id) === queryNumber;
      }
      default:
        return true;
      }
    }
    return false;
  }
  return true;
}

/**
 *
 * @name createDataListAfterFilters
 * @param reports {array} - сервис после объединения с reports и листингом
 * @param filters {object} - объекс с фильтрами
 * @returns {[]}
 */
export const createDataListAfterFilters = (reports, filters) => reports.filter((service) => [
  filterCategories(service, filters.categories),
  filterProvider(service, filters.providers),
  filterStatus(service, filters.status),
  filterQuery(service, filters.query, filters.query_type),
].every((v) => v === true))

/**
 *
 * @name getTotals
 * @description - Считаем totals на фронте
 * @param services {array} - сервис после объединения с reports и листингом
 * @returns {string}
 */
export const getTotals = (services) => {
  const totals = services.reduce((accumulator, currentValue) => {
    accumulator.count_orders += Number(currentValue.count_orders);
    accumulator.charge += Number(currentValue.charge);
    accumulator.cost += Number(currentValue.cost);
    accumulator.profit += Number(currentValue.profit);
    accumulator.refunded += Number(currentValue.refunded);
    return accumulator;
  }, {
    count_orders: 0,
    charge: 0,
    cost: 0,
    profit: 0,
    refunded: 0,
  })

  // eslint-disable-next-line guard-for-in
  for (const key in totals) {
    totals[key] = parseFloat(totals[key].toFixed(5));
  }
  return totals;
}

/**
 *
 * @name getObjectProviders
 * @param providers {array} - список всех провайдер (запрос из раздела сервисов)
 * @returns {{}}
 */
export const getObjectProviders = (providers) => {
  if (providers) {
    const providersObj = {
      0: {
        id: 0,
        title: 'Manual',
        count: 0,
      },
    };

    providers.forEach((provider) => {
      providersObj[provider.id] = {
        id: provider.id,
        title: provider.name,
        count: 0,
      };
    });

    return providersObj;
  }
  return {}
}
/**
 *
 * @name getOptionsProviders
 * @param reports {array} - сервис после объединения с reports и листингом
 * @param providersObject {object} - отформатированный список провайдеров
 * @param filters {object} - объект с фильтрами
 * @returns {{}}
 */
export const getOptionsProviders = (reports, providersObject, filters) => {
  if (!isEmpty(providersObject) && reports?.length) {
    const cloneProvidersObj = cloneDeep(providersObject);
    if (reports?.length) {
      const reportAfterSort = createDataListAfterFilters(reports, filters);
      reportAfterSort.forEach((report) => {
        if (report?.provider_id) {
          cloneProvidersObj[report.provider_id].count++;
        } else {
          cloneProvidersObj[0].count++;
        }
      }, []);
    }
    return Object.values(cloneProvidersObj)
      .map((item) => ({
        ...item,
        title: `${item.title} (${item.count})`,
      }))
      .sort((a, b) => b.count - a.count);
  }

  return []
}

/**
 *
 * @param reports
 * @param filters
 * @returns {[{count: number, id: null, title: string},{count: number, id: number, title: string},{count: number, id: number, title: string}]}
 */
export const getOptionsStatuses = (reports, filters) => {
  const defaultOptions = [
    {
      id: null,
      title: 'All',
      count: 0,
    },
    {
      id: 1,
      title: 'Enabled',
      count: 0,
    },
    {
      id: 0,
      title: 'Disabled',
      count: 0,
    },
  ]
  if (reports?.length) {
    const reportAfterSort = createDataListAfterFilters(reports, filters);
    reportAfterSort.reduce((accumulator, currentValue) => {
      accumulator[0].count++;
      if (currentValue.status) {
        accumulator[1].count++;
      } else {
        accumulator[2].count++;
      }
      return accumulator;
    }, defaultOptions)
  }
  return defaultOptions;
}

/**
 *
 * @param reports
 * @returns {{}|*[]}
 */
export const getObjectCategories = (reports) => {
  let optionsObj = {};
  if (reports?.length) {
    reports.forEach((report) => {
      if (optionsObj[report.category_id]) {
        optionsObj = {
          ...optionsObj,
          [report.category_id]: {
            id: report.category_id,
            title: report.category_title,
            count: 0,
          },
        }
      } else {
        if (report.category_id) {
          optionsObj = {
            ...optionsObj,
            [report.category_id]: {
              id: report.category_id,
              title: report.category_title,
              count: 0,
            },
          }
        } else {
          optionsObj = {
            ...optionsObj,
            0: {
              id: 0,
              title: 'DELETED',
              count: 0,
            },
          }
        }
      }
    });
    return optionsObj;
  }

  return []
}

/**
 *
 * @param reports
 * @param categoriesObject
 * @param filters
 * @returns {(*&{title: string})[]|*[]}
 */
export const getOptionsCategories = (reports, categoriesObject, filters) => {
  if (!isEmpty(categoriesObject)) {
    const cloneCategoryObj = cloneDeep(categoriesObject);
    if (reports?.length) {
      const reportAfterSort = createDataListAfterFilters(reports, filters);
      reportAfterSort.forEach((report) => {
        cloneCategoryObj[report.category_id].count++;
      }, []);
    }
    return Object.values(cloneCategoryObj)
      .map((item) => ({
        ...item,
        title: `${item.title} (${item.count})`,
      }))
      .sort((a, b) => b.count - a.count);
  }

  return []
}

/**
 *
 * @param data
 * @param sort
 * @returns {*}
 */
export const sortData = (data, sort) => {
  const activeSort = sort.columns.find((sort) => sort.active);
  if (activeSort.active === 'desc') {
    return data.sort((a, b) => b[activeSort.name] - a[activeSort.name])
  }
  if (activeSort.active === 'asc') {
    return data.sort((a, b) => a[activeSort.name] - b[activeSort.name])
  }
  return data;
}
